/**
 * Sticky PDP buy block (Cobra-US-StickyPDP experiment)
 *
 * Keeps the product details column (the buy block) pinned to the right of the
 * viewport while the media gallery and the PDP marketing content scroll past on
 * the left, then releases it at the bottom of the marketing content so that
 * product recommendations / recently viewed run full width.
 *
 * The pin itself is native `position: sticky` — this script only measures the
 * geometry that CSS cannot derive on its own and writes it to custom properties
 * on <html>:
 *
 *   --spdp-rail-w      width reserved on the right for the sticky rail
 *   --spdp-rail-h      height of the rail, for pinning a tall one by its bottom
 *   --spdp-mkt-h       height of the marketing section (used to pull it up)
 *   --spdp-grid-min-h  min-height for the product grid, which is what extends
 *                      the sticky containing block down through the marketing
 *                      content
 *
 * See snippets/pdp-sticky-buy-block.liquid for the styles these drive.
 */

const html = document.documentElement;
const config = window.SPDP_CONFIG ?? {};

class StickyBuyBlock {
  /** @type {Record<string, string>} */
  #vars = {};
  /** @type {ReturnType<StickyBuyBlock['collect']>} */
  #refs = null;
  /** @type {Element[]} */
  #observed = [];
  #frame = 0;
  #active = false;
  /** @type {boolean | null} */
  #disclosuresOpen = null;

  constructor() {
    this.mql = window.matchMedia(`(min-width: ${Number(config.breakpoint) || 1400}px)`);
    this.resizeObserver = new ResizeObserver(() => this.schedule());

    const schedule = () => this.schedule();
    const sync = () => this.sync();

    this.mql.addEventListener('change', sync);
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('load', schedule);
    window.addEventListener('pageshow', schedule);

    // Variant changes can swap the whole of <main> (combined listings), which
    // both resizes the rail and invalidates our element references.
    document.addEventListener('variant:update', schedule);

    // Theme editor.
    document.addEventListener('shopify:section:load', sync);
    document.addEventListener('shopify:section:unload', sync);
    document.addEventListener('shopify:section:reorder', sync);
    document.addEventListener('shopify:block:select', schedule);
    document.addEventListener('shopify:block:deselect', schedule);

    document.fonts?.ready.then(schedule);

    sync();
  }

  /**
   * Locates the pieces of the PDP we need. Re-run on every update because morph
   * can replace them underneath us.
   */
  collect() {
    // Scoped to <main> so the quick add modal's copy of the product grid, which
    // lives at the end of <body>, can never be picked up.
    const marketing = document.querySelector('main .pdp-marketing-content');
    const mktSection = marketing?.closest('.shopify-section') ?? null;
    const piSection = document.querySelector('main .product-information');
    const grid = piSection?.querySelector('.product-information__grid') ?? null;
    const media = grid?.querySelector('.product-information__media') ?? null;
    const details = grid?.querySelector('.product-details') ?? null;

    if (!mktSection || !piSection || !grid || !media || !details) return null;

    // Products without marketing metafields render the section empty — there is
    // nothing for the rail to stick past, so leave the page as the theme built it.
    if (marketing.children.length === 0) return null;

    // The media column must sit to the left of the details column. With no
    // media the theme centres the details, and with media on the right the rail
    // would land on the wrong side — leave the theme's own layout alone.
    if (!grid.classList.contains('product-information--media-left')) return null;
    if (!media.firstElementChild) return null;

    mktSection.classList.add('spdp-marketing');
    piSection.closest('.shopify-section')?.classList.add('spdp-product-info');

    return {
      mktSection,
      piSection,
      grid,
      media,
      details,
    };
  }

  /** Turns the experiment on/off for the current viewport and re-measures. */
  sync() {
    const refs = this.mql.matches ? this.collect() : null;

    if (!refs) {
      this.teardown();
      return;
    }

    this.#refs = refs;
    this.#active = true;
    html.classList.add('spdp-on');
    this.openDisclosures(false);
    this.tightenDisclosures();
    this.observe();
    this.update();
  }

  /**
   * Closes up the space between disclosures that follow one another.
   *
   * The rail's row-gap is a merchant setting and differs per template (28px on
   * the fairway, 10px on the custom club builder), and a block whose Liquid
   * resolved to nothing can sit between two disclosures as a hidden wrapper —
   * so neither a fixed offset nor an `+` sibling rule is reliable. Reading the
   * computed gap and skipping hidden siblings handles both.
   *
   * @param {boolean} [reset]
   */
  tightenDisclosures(reset = false) {
    const content = document.querySelector('main .product-details > .group-block > .group-block-content');
    if (!content) return;

    const gap = parseFloat(getComputedStyle(content).rowGap) || 0;
    const target = Number(config.disclosureGap ?? 8);
    const shift = Math.max(0, gap - target);

    let previousWasDisclosure = false;
    for (const child of content.children) {
      if (!(child instanceof HTMLElement)) continue;
      if (getComputedStyle(child).display === 'none') continue;

      const isDisclosure = Boolean(child.querySelector(':scope > .spdp-disclosure'));
      child.style.marginBlockStart = !reset && isDisclosure && previousWasDisclosure ? `-${shift}px` : '';
      previousWasDisclosure = isDisclosure;
    }
  }

  /**
   * The description / SPECS / trade-in blocks are authored as <details> in the
   * product template so they collapse inside the pinned rail. Off the rail
   * there is no height pressure and hiding that content would be a regression,
   * so they are forced open instead.
   *
   * Only touched when the active state actually flips, so a reader's own
   * expand/collapse is never overridden by a resize or a re-measure.
   *
   * In the rail they share a `name`, which makes the browser keep only one of
   * them open at a time. Re-applied on every pass because morph can swap the
   * elements; removed off the rail, where a shared name would stop them all
   * being open together.
   *
   * @param {boolean} open
   */
  openDisclosures(open) {
    const disclosures = document.querySelectorAll('main .spdp-disclosure');
    for (const el of disclosures) {
      if (open) el.removeAttribute('name');
      else el.name = 'spdp-disclosure';
    }

    if (this.#disclosuresOpen === open) return;
    this.#disclosuresOpen = open;
    for (const el of disclosures) el.open = open;
  }

  /** Coalesces bursts of resize/mutation notifications into one measure pass. */
  schedule() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.sync();
    });
  }

  teardown() {
    this.openDisclosures(true);
    this.tightenDisclosures(true);

    if (!this.#active && !html.classList.contains('spdp-on')) return;

    this.#active = false;
    this.#refs = null;
    html.classList.remove('spdp-on');
    this.resizeObserver.disconnect();
    this.#observed = [];

    for (const name of Object.keys(this.#vars)) html.style.removeProperty(name);
    this.#vars = {};
  }

  observe() {
    const targets = [this.#refs.media, this.#refs.details, this.#refs.mktSection];
    const unchanged =
      targets.length === this.#observed.length && targets.every((el, i) => el === this.#observed[i]);

    if (unchanged) return;

    this.resizeObserver.disconnect();
    for (const target of targets) this.resizeObserver.observe(target);
    this.#observed = targets;
  }

  /**
   * @param {string} name
   * @param {number} px
   */
  set(name, px) {
    const value = `${Math.round(px)}px`;
    if (this.#vars[name] === value) return;
    this.#vars[name] = value;
    html.style.setProperty(name, value);
  }

  update() {
    if (!this.#active || !this.#refs) return;

    const { piSection, media, details, mktSection } = this.#refs;

    // 1. Rail width — everything to the right of the media column's content box
    //    belongs to the sticky buy block, including the page's right margin.
    const sectionRight = piSection.getBoundingClientRect().right;
    const mediaRect = media.getBoundingClientRect();
    const mediaPadRight = parseFloat(getComputedStyle(media).paddingRight) || 0;
    const mediaContentRight = mediaRect.right - mediaPadRight;

    this.set('--spdp-rail-w', Math.max(0, sectionRight - mediaContentRight));

    //    Rail height, so a rail taller than the window can pin by its bottom
    //    edge (see `top` in the snippet). Opening a disclosure or switching
    //    builder step resizes it, which the ResizeObserver picks up.
    this.set('--spdp-rail-h', details.offsetHeight);

    // 2. Marketing height. Reading offsetHeight here forces the reflow that
    //    applies the rail width above, so this is the narrowed height.
    const mktHeight = mktSection.offsetHeight;
    this.set('--spdp-mkt-h', mktHeight);

    // 3. Extend the grid — and therefore the details column's sticky containing
    //    block — so it ends level with the bottom of the marketing content.
    //
    //    Measured off the media column rather than the taller of the two, so the
    //    marketing content starts directly under the gallery instead of under
    //    the (usually much longer) buy block. Both columns are `align-self: start`,
    //    so their heights stay natural and this cannot feed back into itself.
    this.set('--spdp-grid-min-h', media.offsetHeight + mktHeight);
  }
}

if (config.enabled !== false) new StickyBuyBlock();
